"use client"

import { useRouter, usePathname } from "next/navigation"
import { startNavigationLoading } from "@src/utils/navigation-loading"

function normalizePath(path: string) {
  if (!path) return "/"
  const [pathname, search = ""] = path.split("?")
  const clean = pathname.replace(/\/$/, "") || "/"
  return `${clean}${search ? `?${search}` : ""}`
}

function stripLocale(path: string) {
  return path.replace(/^\/(vi|en)(?=\/|$)/, "") || "/"
}

/**
 * Next App Router helper: starts nprogress before client navigations
 * that use router.push/replace (buttons, not <a href>).
 */
export function useAppRouter() {
  const router = useRouter()
  const pathname = usePathname()

  const shouldShowLoading = (href: string) => {
    try {
      // Use full href as base so relative "?foo=1" keeps the current path.
      const url = new URL(href, window.location.href)
      const next = normalizePath(`${url.pathname}${url.search}`)
      const current = normalizePath(`${pathname}${window.location.search}`)
      return stripLocale(next) !== stripLocale(current)
    } catch {
      return true
    }
  }

  return {
    ...router,
    push: (...args: Parameters<typeof router.push>) => {
      const href = String(args[0] ?? "")
      if (href && !href.startsWith("#") && shouldShowLoading(href)) {
        startNavigationLoading()
      }
      return router.push(...args)
    },
    replace: (...args: Parameters<typeof router.replace>) => {
      const href = String(args[0] ?? "")
      if (href && !href.startsWith("#") && shouldShowLoading(href)) {
        startNavigationLoading()
      }
      return router.replace(...args)
    }
  }
}
